import { Alert, AlertTitle } from '@mui/material'
import moment from 'moment'
import { useEffect, useState } from 'react'
import type { FC } from 'react'
import styled from 'styled-components'

import CommentsLabel from '@/components/CommentsLabel'
import CommentsSidebar from '@/components/CommentsSidebar'
import DataRequests from '@/components/DataRequests'
import { ActionButtonContainer } from '@/components/DecisionPanelCards/Shared'
import {
  displayFailReasons,
  displaySuccessReasons,
} from '@/components/DecisionPanelCards/utils'
import DetailsList from '@/components/DetailsList'
import DocumentRequests from '@/components/DocumentRequests'
import ExpandableCard from '@/components/ExpandableCard'
import Lightbox, { getFile } from '@/components/Lightbox'
import OutcomeStatusPill from '@/components/OutcomeStatusPill'
import ProsCons from '@/components/ProsCons/ProsCons'
import ReopenSection from '@/components/ReopenSection/ReopenSection'
import { SectionName } from '@/components/ReopenSection/types'
import SanctionsOverview from '@/components/Sanctions/Overview'
import VerifyId from '@/components/VerifyId'
import type { FetchSingleApplication } from '@/hooks/useSubject'
import { OutcomeSectionName } from '@/lib/apollo/model/outcome'
import type { IdentityOutcome } from '@/lib/apollo/model/outcome'
import type { Applicant } from '@/lib/apollo/query/get-applicant'
import { IdentityProvider, IDVT } from '@/types/Activity'
import type { Application } from '@/types/Activity'
import type { Touchpoint } from '@/types/Touchpoints'
import { isUnsetDate } from '@/utils/date'
import { isIdEvidenceDocument, isSelfieDocument } from '@/utils/documents'
import { capitalize } from '@/utils/helpers'
import { isFinalOutcome } from '@/utils/outcome'
import { isRefCompleted, isTenant, isCorporateTenant } from '@/utils/subjectHelpers'
import { isCorporateTier } from '@/utils/tier'
import { useUserTracking } from '@/utils/tracking'

import OverrideOutcomes from './OverrideOutcomes'
import RightToRent from './RightToRent'

const NoIDWrapper = styled.div`
  width: 100%;
`

const Capitalized = styled.span`
  text-transform: capitalize;
`

export const Body = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 20px;
  transition: height 0.25s ease-in;
`

const DetailsContainer = styled.div`
  margin-top: 20px;
`

const EntityContainer = styled.div`
  grid-gap: 15px;
  display: flex;
  flex-flow: row wrap;
  div:nth-child(1) {
    flex: 1 1 300px;
  }
  div:nth-child(2) {
    flex: 1 1 300px;
    min-width: 200px;
  }
  span {
    font-family: 'Montserrat', sans-serif;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
  }

  > div {
    display: flex;
    flex-flow: row;
    column-gap: 10px;
    max-width: 50%;

    > div:nth-child(1) {
      display: flex;
      flex: 1 1 30%;
      max-width: 30%;
    }
    > div:nth-child(2) {
      flex: 1 1 60%;
      max-width: 60%;
    }
  }

  img {
    border-radius: 10px;
    max-width: 300px;
    object-fit: cover;
    height: auto;
    width: 100%;
    max-height: 150px;
  }
`

const LargeImage = styled.img`
  max-width: 100%;
  min-width: 125px;
  height: auto;
  border-radius: 10px;
  flex: 1;
  object-fit: contain;
  cursor: pointer;
`

export const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
  gap: 20px;
`

interface Props {
  applicant: Applicant
  application: Application
  fetchSingleApplication: FetchSingleApplication
  isLoadingTouchpoints: boolean
  subjectTouchpoints: Touchpoint[]
  fetchSubjectTouchpoints: () => Promise<void>
  isRestrictedReference: boolean
}

const IdentityCard: FC<Props> = ({
  applicant,
  application,
  fetchSingleApplication,
  isLoadingTouchpoints,
  subjectTouchpoints,
  fetchSubjectTouchpoints,
  isRestrictedReference,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  const entity = application.identityData

  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxStartIndex, setLightboxStartIndex] = useState<number>(0)

  const [documentMeta, setDocumentMeta] = useState<any[]>([])
  const [personalMeta, setPersonalMeta] = useState<any[]>([])
  const [identityData, setIdentityData] = useState<any[]>([])

  const { trackUserActivity } = useUserTracking()

  const [commentsSidebarOpen, setCommentsSidebarOpen] = useState<boolean>(false)

  const outcomeSections = application?.outcome?.sections ?? []
  const identityOutcome = outcomeSections.find(
    outcomeSection => outcomeSection.name === OutcomeSectionName.identity,
  ) as IdentityOutcome | undefined

  const overallOutcomeStatus = application.outcome?.status
  const identityOutcomeStatus = identityOutcome?.status

  const [identityFail, setIdentityFail] = useState<string[]>([])
  const [identitySuccess, setIdentitySuccess] = useState<string[]>([])

  const sectionComments = subjectTouchpoints.filter(
    ({ category }) => category.toLowerCase() === 'identity',
  )

  const referenceTier = applicant.referenceTier

  const hasIDVTChecks =
    application.IDVTIDCheckStatus !== undefined && application.IDVTIDCheckStatus !== null

  const isCorporateTenantSubject = isCorporateTenant(
    application.type,
    application.context?.journeyType,
  )

  const shouldDisplayOverrides =
    !isCorporateTenantSubject && !hasIDVTChecks && !isRestrictedReference

  const applicantIsIDVTRequired =
    application.journeyConfig?.idvt === IDVT.Required ||
    application.journeyConfig?.idvt === IDVT.UKOnly

  useEffect(() => {
    if (
      application &&
      application.results !== undefined &&
      application.results !== null &&
      application.results.length > 0
    ) {
      setIdentitySuccess(displaySuccessReasons(application.results, 'identity'))
      setIdentityFail(displayFailReasons(application.results, 'identity'))
    }
  }, [application, application.results])

  useEffect(() => {
    if (!application || !application?.context) {
      return
    }

    let documentInfo: any[] = []
    let personalInfo: any[] = []
    let identityInfo: any[] = []

    if (application && application.context && application.context.identity) {
      const nationality =
        application?.context?.country?.nationality ||
        application?.context?.nationality ||
        ''

      const { mobile, email, dob, firstName, lastName, middleName } = application?.context

      const { facialMatch, idType, idNumber, nationalInsuranceNumber } =
        application?.context.identity

      const company = application?.context?.company

      identityInfo = [
        {
          key: 'Name',
          value: (
            <span>
              <Capitalized>{firstName ? capitalize(firstName) : ''} </Capitalized>
              <Capitalized>{middleName ? capitalize(middleName) : ''} </Capitalized>
              <Capitalized>{lastName ? capitalize(lastName) : ''}</Capitalized>
            </span>
          ),
        },
        { key: 'Email', value: email || '' },
        { key: 'Mobile', value: mobile || '' },
        ...(isCorporateTier(referenceTier) && company
          ? [{ key: 'Company', value: company.name }]
          : []),
      ]

      const hasDateOfBirth = !isUnsetDate(dob)

      personalInfo = [
        {
          key: 'Nationality',
          value: nationality !== '' ? nationality : hasDateOfBirth ? 'Unknown' : '',
        },
        {
          key: 'Date of birth',
          value: hasDateOfBirth ? moment.utc(dob).format('Do MMMM YYYY') : '',
        },
        ...(nationalInsuranceNumber
          ? [
              {
                key: 'National Insurance Number',
                value: nationalInsuranceNumber || '',
              },
            ]
          : []),
      ]

      documentInfo = [
        {
          key: 'Document Type',
          value: idType ? idType : 'Unknown',
        },
        {
          key: 'ID Number',
          value: idNumber ? idNumber : 'Not found',
        },
        {
          key: 'Facial Match',
          value: `${facialMatch === true ? 'Yes' : 'No'}` || '',
        },
      ]
    }

    setDocumentMeta(documentInfo)
    setPersonalMeta(personalInfo)
    setIdentityData(identityInfo)
  }, [application, referenceTier])

  if (
    !(
      application.identityData &&
      application.identityData.items &&
      application.identityData.items.length > 0
    )
  ) {
    return <></>
  }

  const documents = application?.context?.documents ?? []

  const idEvidenceDocuments = documents.filter(isIdEvidenceDocument(application))

  const selfieDocuments = documents.filter(isSelfieDocument)

  const firstSelfieDocument = selfieDocuments.length > 0 ? selfieDocuments[0] : undefined

  const studentIDDocument = documents.find(file => file.specifiedType === 'Student ID')
  const studentIDContext = application.studentIDContext

  const handleOpenIdEvidence = (lightboxIndex: number) => {
    setLightboxStartIndex(lightboxIndex)
    setLightboxOpen(true)
    trackUserActivity('View document', {
      subjectId: application.subjectId,
      customerId: application.customerId,
      fileName: getFile(documents[lightboxIndex]),
    })
  }

  return (
    <>
      {lightboxOpen && (
        <Lightbox
          images={documents}
          onClose={() => setLightboxOpen(false)}
          startIndex={lightboxStartIndex}
          customerId={application.customerId}
          subjectId={application.subjectId}
        />
      )}
      <CommentsSidebar
        isOpen={commentsSidebarOpen}
        application={application}
        onClose={() => setCommentsSidebarOpen(false)}
        touchpoints={sectionComments}
        activeEntity="IDENTITY"
        fetchSubjectTouchpoints={fetchSubjectTouchpoints}
      />
      <ExpandableCard
        isExpanded={isExpanded}
        handleExpand={() => setIsExpanded(!isExpanded)}
        label="Identity"
        icon="identity"
        actionButton={
          <ActionButtonContainer>
            <div>
              <CommentsLabel
                isLoadingTouchpoints={isLoadingTouchpoints}
                onToggle={() => setCommentsSidebarOpen(!commentsSidebarOpen)}
                sectionComments={sectionComments}
              />
            </div>

            {overallOutcomeStatus && identityOutcomeStatus && (
              <OutcomeStatusPill
                overallOutcomeStatus={overallOutcomeStatus}
                outcomeStatus={identityOutcomeStatus}
              />
            )}
          </ActionButtonContainer>
        }
        expandedContent={
          <Body>
            <ButtonsContainer>
              {applicantIsIDVTRequired && (
                <DataRequests
                  customerId={application.customerId}
                  applicationId={application.applicationId}
                  subjectId={application.subjectId}
                  subjectName={application.name}
                  hasOutcome={isFinalOutcome(identityOutcomeStatus)}
                  disabled={isRestrictedReference}
                />
              )}
              <DocumentRequests
                customerId={application.customerId}
                applicationId={application.applicationId}
                subjectId={application.subjectId}
                sectionName={SectionName.identity}
                subjectName={`${application.context?.firstName} ${application.context?.lastName}`}
                isRefCompleted={isRefCompleted(application)}
                isCorporateTenantSubject={isCorporateTenantSubject}
                disabled={isRestrictedReference}
              />
              {application.applicationId &&
                application.subjectId &&
                isFinalOutcome(identityOutcomeStatus) && (
                  <ReopenSection
                    customerId={application.customerId}
                    applicationId={application.applicationId}
                    subjectId={application.subjectId}
                    sectionName={SectionName.identity}
                    disabled={isRestrictedReference}
                  />
                )}
              <VerifyId
                customerId={application.customerId}
                applicationId={application.applicationId}
                subjectId={application.subjectId}
                fullName={applicant.fullName}
                firstName={applicant.firstName}
                middleName={applicant.middleName ?? undefined}
                lastName={applicant.lastName}
                nationality={application.context?.nationality}
                subjectType={applicant.type}
                dateOfBirth={applicant.dateOfBirth ?? null}
                r2RShareCode={applicant.r2RShareCode}
                companyNamedOfficer={applicant.companyNamedOfficer}
                hasR2RChecks={
                  application.IDVTR2RStatus !== undefined &&
                  application.IDVTR2RStatus !== null &&
                  application.IDVTR2RStatus !== 'NotPerformed'
                }
                hasIdentityChecks={
                  application.journeyConfig?.identityProvider ===
                    IdentityProvider.Credas ||
                  application.journeyConfig?.identityProvider === IdentityProvider.Entrust
                }
                documents={applicant.documents}
                idEvidenceDocuments={applicant.idEvidenceDocuments}
                idType={applicant.idType}
                referenceTier={referenceTier}
                onSuccess={() =>
                  fetchSingleApplication(application.customerId, application.subjectId)
                }
                identityVerification={application.context?.identity?.verification}
                letterOfAuthorityProvided={applicant.letterOfAuthorityProvided ?? null}
                facialMatch={applicant.facialMatch ?? false}
                sanctions={applicant?.sanctions ?? undefined}
                overallOutcomeStatus={overallOutcomeStatus}
                identityProvider={application.journeyConfig?.identityProvider}
              />
            </ButtonsContainer>
            {applicantIsIDVTRequired && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <AlertTitle>IDVT is required for this applicant</AlertTitle>
              </Alert>
            )}
            <ProsCons pros={identitySuccess} cons={identityFail} />
            <DetailsContainer>
              <DetailsList
                details={[...identityData, ...personalMeta].map(({ key, value }) => ({
                  label: key,
                  children: value,
                }))}
              />
            </DetailsContainer>

            <div style={{ marginTop: '10px' }}>
              <SanctionsOverview sanctions={applicant.sanctions} />
            </div>

            <div>
              {entity !== undefined && entity.documentProof && (
                <>
                  <h2
                    style={{
                      fontSize: '14px',
                      marginTop: '30px',
                    }}
                  >
                    Evidence
                  </h2>
                  <EntityContainer>
                    {idEvidenceDocuments.map((idEvidenceDocument, i) => (
                      <div key={i}>
                        <LargeImage
                          src={
                            idEvidenceDocument.watermarkedThumbnail ??
                            idEvidenceDocument.thumbnail
                          }
                          alt=""
                          onClick={() =>
                            handleOpenIdEvidence(documents.indexOf(idEvidenceDocument))
                          }
                        />
                        <div>
                          {documentMeta.map(({ key, value }, i) => {
                            return (
                              <span key={i}>
                                <strong>{key}</strong>: {value}
                                <br />
                              </span>
                            )
                          })}
                        </div>
                      </div>
                    ))}

                    {firstSelfieDocument !== undefined && (
                      <div>
                        <LargeImage
                          src={firstSelfieDocument.thumbnail}
                          alt=""
                          onClick={() =>
                            handleOpenIdEvidence(documents.indexOf(firstSelfieDocument))
                          }
                        />
                        <div>
                          <span>
                            <strong>Document Type</strong>: Selfie <br />
                          </span>
                        </div>
                      </div>
                    )}

                    {studentIDDocument !== undefined ? (
                      <div>
                        <LargeImage
                          src={studentIDDocument.thumbnail}
                          alt=""
                          onClick={() =>
                            handleOpenIdEvidence(documents.indexOf(studentIDDocument))
                          }
                        />
                        <div>
                          <span>
                            <strong>Document Type</strong>: Student ID <br />
                          </span>
                        </div>
                      </div>
                    ) : (
                      studentIDContext !== undefined &&
                      studentIDContext !== null && (
                        <NoIDWrapper>
                          <span>
                            <strong>No student ID provided</strong>: {studentIDContext}
                            <br />
                          </span>
                        </NoIDWrapper>
                      )
                    )}
                  </EntityContainer>
                </>
              )}
            </div>

            {!isCorporateTenantSubject && isTenant(application.type) && (
              <RightToRent application={application} />
            )}

            {shouldDisplayOverrides && (
              <OverrideOutcomes
                applicationId={application.applicationId}
                customerId={application.customerId}
                subjectId={application.subjectId}
                subjectOutcome={application.outcome}
              />
            )}
          </Body>
        }
      />
    </>
  )
}

export default IdentityCard
